// ─────────────────────────────────────────────────────────────────────────────
// RTK Query base query factory
//
// Creates a BaseQueryFn from any Axios instance. Each feature API module
// picks the right instance (pension / identity / salary).
//
// Responsibilities:
//   - Executes the request via the authenticated Axios instance
//   - Unwraps the ApiResponse envelope (returns `body.data` as the RTK data)
//   - Handles business-level failures (HTTP 200 but `succeed: false`)
//   - Returns a standardised ApiError on any failure
//
// Toast notifications are NOT handled here — they are handled by:
//   - The global rtkQueryErrorToastMiddleware in store.ts (default)
//   - Individual components using .unwrap() + try/catch (override)
// ─────────────────────────────────────────────────────────────────────────────

import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import type { ApiError } from '../types/api';
import { pensionAxios, identityAxios, salaryAxios } from './axios';

type BaseQueryArgs = {
  url: string;
  method?: AxiosRequestConfig['method'];
  data?: unknown;
  params?: unknown;
  responseType?: AxiosRequestConfig['responseType'];
  /** When true, the global error toast listener will be suppressed for this call. */
  suppressGlobalErrorToast?: boolean;
};

export const makeAxiosBaseQuery =
  (axiosInstance: AxiosInstance): BaseQueryFn<BaseQueryArgs, unknown, ApiError> =>
  async ({ url, method = 'GET', data, params, responseType }) => {
    try {
      const result = await axiosInstance({ url, method, data, params, responseType });
      const body = result.data;

      // Handle business-level failures: HTTP 200 but succeed === false
      if (body?.succeed === false) {
        return {
          error: {
            message: body.message || 'Operation failed.',
            isBusinessError: true,
          } as ApiError,
        };
      }

      // Unwrap the ApiResponse envelope — RTK Query cache receives `body.data`
      // Fall back to the raw body when there is no `data` key (some endpoints
      // return the payload at the top level).
      return { data: body?.data ?? body };
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>;
      return {
        error: {
          status: error.response?.status,
          message:
            error.response?.data?.message ??
            (error.message === 'Network Error'
              ? 'Network Error — please check your connection.'
              : error.message ?? 'An unexpected error occurred.'),
        } as ApiError,
      };
    }
  };

// Pre-built base queries — one per service domain
export const pensionBaseQuery  = makeAxiosBaseQuery(pensionAxios);
export const identityBaseQuery = makeAxiosBaseQuery(identityAxios);
export const salaryBaseQuery   = makeAxiosBaseQuery(salaryAxios);
